import { DiffinderIcon } from '@df/ui/components/logos';
import FortnoxSmall from '$components/logo/fortnox_small.svelte';
import VismaSmall from '$components/logo/visma_small.svelte';
import { logError } from '$lib/log_error';
import { randomId } from '$lib/random';
import { bookYearSchema } from '$types/schemas';
import { caseIdSchema, clientIdSchema, employerIdSchema, Time, timeStampSchema } from '@df/common';
import { createLocalStorageStoreWithZod, isSessionEnding } from '@df/utils';
import type { Result } from 'neverthrow';
import type { Component } from 'svelte';
import type { HTMLAttributeAnchorTarget } from 'svelte/elements';
import type { Readable } from 'svelte/store';
import { derived, writable } from 'svelte/store';
import { z } from 'zod';

type Kind = z.infer<typeof kindSchema>;
const kindSchema = z.enum(['error', 'info', 'success', 'warning']);

type HrefAction = { type: 'Href'; url: URL; context: string; target?: HTMLAttributeAnchorTarget };
type ClickAction = { type: 'Click'; click: () => void };

type Action = HrefAction | ClickAction;

type Toast = z.infer<typeof toastSchema>;
const toastSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Text'),
    message: z.string(),
    kind: kindSchema,
    timeout: z.number().optional(),
  }),
  z.object({
    type: z.literal('NotSaved'),
    url: z.string(),
    context: z.string(),
    detail: z.string().optional(),
  }),
]);

type ProcessResult = Result<void, { message: string; action?: Action }>;
type ProcessType = 'Ledger' | 'Fortnox' | 'Spiris' | 'Sharepoint';
function translateProcessType(type: ProcessType): string {
  switch (type) {
    case 'Ledger':
      return 'Hämtar bokföringsdata...';
    case 'Fortnox':
      return 'Hämtar data från Fortnox...';
    case 'Spiris':
      return 'Hämtar data från Spiris...';
    case 'Sharepoint':
      return 'Hämtar data från Sharepoint...';
  }
}

type Process = {
  type: ProcessType;
  promise: Promise<ProcessResult>;
  resolved: 'Resolved' | 'Rejected' | undefined;
};

type ToastKind = z.infer<typeof toastKindSchema>;
const toastKindSchema = z.object({
  type: z.literal('Toast'),
  data: toastSchema,
});

type ProcessKind = { type: 'Process'; data: Process };
type NotificationKind = ToastKind | ProcessKind;

type NotificationId = z.infer<typeof notificationIdSchema>;
const notificationIdSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Case'),
    id: caseIdSchema,
  }),
  z.object({
    type: z.literal('Client'),
    id: clientIdSchema,
    bookYear: bookYearSchema.optional(),
  }),
  z.object({
    type: z.literal('Employer'),
    id: employerIdSchema,
  }),
  z.object({
    type: z.literal('System'),
  }),
]);

type NotificationMetadata = z.infer<typeof notificationMetadataSchema>;
const notificationMetadataSchema = z.object({
  id: z.string(),
  createdAt: timeStampSchema,
  ttl: z.number().optional(),
});

type Notification<TKind extends NotificationKind = NotificationKind> = z.infer<
  ReturnType<typeof notificationSchema<TKind>>
>;
const notificationSchema = <TKind extends NotificationKind>(kindSchema: z.ZodType<TKind>) =>
  z.object({
    id: notificationIdSchema,
    meta: notificationMetadataSchema,
    kind: kindSchema,
  });

function getToastTimeout(toast: Toast): number {
  switch (toast.type) {
    case 'Text':
      return toast.timeout ?? TIMEOUT;
    case 'NotSaved':
      return TIMEOUT;
  }
}

const TIMEOUT = 5000;
const PERSISTENCE_SIZE = 100 as const;

function createNotificationStore() {
  const _toasts = createLocalStorageStoreWithZod(
    'toasts',
    z.array(notificationSchema(toastKindSchema)),
  );
  const _timedToasts = writable<Notification<ToastKind>[]>([]);
  const _processes = writable<Notification<ProcessKind>[]>([]);

  function sendNotification(
    notification: Notification<ToastKind>,
    reset: boolean,
    persist: boolean,
  ) {
    if (persist) {
      _toasts.update((state) => {
        if (!state) return [notification];

        return [notification, ...state.slice(0, PERSISTENCE_SIZE)];
      });
    }

    const kind = notification.kind;
    if (reset) {
      if (kind.type === 'Toast') {
        _timedToasts.update(() => {
          return [{ ...notification, kind }];
        });
      }
    } else {
      if (kind.type === 'Toast') {
        _timedToasts.update((state) => {
          return [...state, { ...notification, kind }];
        });
      }
    }
  }

  function send(notificationId: NotificationId, data: Toast, reset: boolean, persist = true) {
    // Ending the session clears storage before it navigates, so anything sent
    // after that repopulates `toasts` and outlives the session it belongs to.
    if (isSessionEnding()) {
      return;
    }

    const notification = {
      id: notificationId,
      meta: { id: id(), createdAt: Time.Timestamp.now(), ttl: Time.Milliseconds.minute * 30 },
      kind: { type: 'Toast', data },
    } satisfies Notification;

    sendNotification(notification, reset, persist);
  }

  function process(
    notificationId: NotificationId,
    type: ProcessType,
  ): {
    promise: Promise<ProcessResult>;
    resolve: (value: ProcessResult) => void;
    reject: (reason?: unknown) => void;
  } {
    const { promise, resolve, reject } = Promise.withResolvers<ProcessResult>();
    const processId = id();

    _processes.update((state) => {
      return [
        {
          id: notificationId,
          meta: {
            id: processId,
            createdAt: Time.Timestamp.now(),
            ttl: Time.Milliseconds.minute * 30,
          },
          kind: { type: 'Process', data: { type, promise, resolved: undefined } },
        },
        ...state.slice(0, PERSISTENCE_SIZE),
      ];
    });

    // Set resolved true when promise is resolved
    promise
      .then((result) => {
        if (result.isErr()) {
          const { message } = result.error;
          send({ type: 'System' }, { type: 'Text', kind: 'error', message }, false, false);
        }

        _processes.update((state) => {
          state = state.map((n) => {
            const id = n.meta.id;
            const kind = n.kind;
            return id === processId && kind.type === 'Process'
              ? {
                  ...n,
                  kind: {
                    type: 'Process',
                    data: { ...kind.data, resolved: result.isOk() ? 'Resolved' : 'Rejected' },
                  },
                }
              : n;
          });

          return state;
        });
      })
      .catch((e) => {
        _processes.update((state) => {
          state = state.map((n) => {
            const id = n.meta.id;
            const kind = n.kind;
            return id === processId && kind.type === 'Process'
              ? { ...n, kind: { type: 'Process', data: { ...kind.data, resolved: 'Rejected' } } }
              : n;
          });

          return state;
        });

        logError(e, 'Process error');
      });

    return { promise, resolve, reject };
  }

  const timedToasts: Readable<Notification<ToastKind>[]> = derived(
    _timedToasts,
    ($_timedToasts, set) => {
      set($_timedToasts);
      if ($_timedToasts.length > 0) {
        const toast = $_timedToasts[0];
        const timeout = getToastTimeout(toast.kind.data);

        const timer = setTimeout(() => {
          _timedToasts.update((state) => {
            state = state.filter((n) => n.meta.id !== toast.meta.id);

            return state;
          });
        }, timeout);

        return () => {
          clearTimeout(timer);
        };
      }
    },
  );

  const processes: Readable<Notification<ProcessKind>[]> = derived(
    _processes,
    ($_processes, set) => {
      const processes = $_processes.filterMap((p) =>
        p.kind.type === 'Process' && p.kind.data.resolved !== 'Resolved'
          ? { ...p, kind: p.kind }
          : undefined,
      );
      set(processes);
    },
  );

  function deleteToast(id: string) {
    _toasts.update((state) => {
      if (!state) return [];
      state = state.filter((n) => n.meta.id !== id);

      return state;
    });
  }

  function deleteProcess(id: string) {
    _processes.update((state) => {
      if (!state) return [];
      state = state.filter((n) => n.meta.id !== id);

      return state;
    });
  }

  function clear() {
    _toasts.set([]);
    _processes.set([]);
  }

  const systemId = { type: 'System' } as const;

  return {
    toasts: { ..._toasts, delete: deleteToast },
    timedToasts,
    processes: { ...processes, delete: deleteProcess },
    clear,
    default: (msg: string, reset = false, timeout = TIMEOUT) =>
      send(systemId, { type: 'Text', kind: 'info', message: msg, timeout }, reset),
    error: (msg: string, reset = false, timeout = TIMEOUT) =>
      send(systemId, { type: 'Text', kind: 'error', message: msg, timeout }, reset),
    success: (msg: string, reset = false, timeout = TIMEOUT) =>
      send(systemId, { type: 'Text', kind: 'success', message: msg, timeout }, reset),
    warning: (msg: string, reset = false, timeout = TIMEOUT) =>
      send(systemId, { type: 'Text', kind: 'warning', message: msg, timeout }, reset),

    // New
    toast: (data: Toast, id: NotificationId, opts: { reset: boolean } = { reset: false }) =>
      send(id, data, opts.reset),
    process,
  };
}

function id() {
  return 'notification_' + randomId();
}

type ToastView = {
  message: string;
  kind: Kind;
  createdAt: Time.Timestamp;
  action: Action | undefined;
};

function getToast(toast: Toast, metadata: NotificationMetadata): ToastView {
  switch (toast.type) {
    case 'Text':
      return {
        message: toast.message,
        kind: toast.kind,
        createdAt: metadata.createdAt,
        action: undefined,
      };
    case 'NotSaved': {
      const prefix = 'Ändringarna sparades inte';
      return {
        message: `${prefix}${toast.detail !== undefined ? `. ${toast.detail}` : ''}`,
        kind: 'warning',
        createdAt: metadata.createdAt,
        action: { type: 'Href', url: new URL(toast.url), context: toast.context },
      };
    }
  }
}

type ProcessView = {
  message: string;
  promise: Promise<ProcessResult>;
  resolved: 'Resolved' | 'Rejected' | undefined;
  icon: Component<{ size: 16 | 20 }>;
  createdAt: Time.Timestamp;
  action: Action | undefined;
};

function getProcess(process: Process, metadata: NotificationMetadata): ProcessView {
  return {
    message: translateProcessType(process.type),
    promise: process.promise,
    resolved: process.resolved,
    icon:
      process.type === 'Ledger'
        ? DiffinderIcon
        : process.type === 'Fortnox'
          ? FortnoxSmall
          : VismaSmall,
    createdAt: metadata.createdAt,
    action: undefined,
  };
}
type NotificationView = { type: 'Toast'; view: ToastView } | { type: 'Process'; view: ProcessView };

export const notifications = createNotificationStore();

export { getProcess, getToast };
export type { Action as NotificationAction, NotificationView, ProcessResult };
